import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import type { AppState } from '@/types';
import { authService, type User } from '@/services';
import { azureAuthService } from '@/services/azureAuthService';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

interface AppContextValue {
  state: AppState;
  loginWithEmail: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  loginWithGoogle: () => void;
  loginWithMicrosoft: () => Promise<{ success: boolean; error?: string }>;
  handleOAuthCallback: (token: string, provider: string) => Promise<boolean>;
  logout: () => void;
  selectCourse: (courseId: string) => void;
  setLabMode: (mode: 'guided' | 'free') => void;
  isLoading: boolean;
  user: User | null;
}

const AppContext = createContext<AppContextValue | null>(null);

const initialState: AppState = {
  isAuthenticated: false,
  authMode: null,
  studentInfo: null,
  selectedCourseId: null,
  labMode: 'guided',
};

const STORAGE_KEY = 'virtuallab_app';

export function AppProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AppState>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        return { ...initialState, ...JSON.parse(saved) };
      }
    } catch (e) {
      console.error('Failed to load app state:', e);
    }
    return initialState;
  });
  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  // Check for existing token on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = authService.getStoredToken();
      if (token && state.isAuthenticated) {
        try {
          const verifiedUser = await authService.verifyToken();
          if (verifiedUser) {
            setUser(verifiedUser);
          } else {
            setState(initialState);
            setUser(null);
          }
        } catch {
          // Backend unavailable, keep local auth state
        }
      }
    };
    checkAuth();
  }, []);

  // Listen for auth:logout events from API service (e.g., 401 responses)
  useEffect(() => {
    const handleAuthLogout = () => {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem('virtuallab_progress');
      setState(initialState);
      setUser(null);
    };
    window.addEventListener('auth:logout', handleAuthLogout);
    return () => window.removeEventListener('auth:logout', handleAuthLogout);
  }, []);

  // Unified email/password login
  const loginWithEmail = useCallback(async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    setIsLoading(true);
    try {
      const result = await authService.login({ email, password });
      if (result) {
        setUser(result.user);
        const authMode = result.user.role === 'student' ? 'student' : 'instructor';
        setState(prev => ({
          ...prev,
          isAuthenticated: true,
          authMode,
        }));
        setIsLoading(false);
        return { success: true };
      }
      setIsLoading(false);
      return { success: false, error: 'Invalid email or password' };
    } catch (error: any) {
      setIsLoading(false);
      return { success: false, error: error?.message || 'Login failed' };
    }
  }, []);

  // Google OAuth - redirect to Google consent screen
  const loginWithGoogle = useCallback(() => {
    const callbackUrl = encodeURIComponent(`${window.location.origin}/#/auth/callback`);
    window.location.href = `${API_URL}/auth/google/login?redirect_uri=${callbackUrl}`;
  }, []);

  // Microsoft OAuth - MSAL popup flow
  const loginWithMicrosoft = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    setIsLoading(true);
    try {
      const result = await azureAuthService.loginWithMicrosoft();
      setUser(result.user);
      const authMode = result.user.role === 'student' ? 'student' : 'instructor';
      setState(prev => ({
        ...prev,
        isAuthenticated: true,
        authMode,
      }));
      setIsLoading(false);
      return { success: true };
    } catch (error: any) {
      setIsLoading(false);
      return { success: false, error: error?.message || 'Microsoft login failed' };
    }
  }, []);

  // Handle OAuth callback (after Google redirect)
  const handleOAuthCallback = useCallback(async (token: string, provider: string): Promise<boolean> => {
    setIsLoading(true);
    try {
      const verifiedUser = await authService.handleOAuthCallback(token);
      if (verifiedUser) {
        setUser(verifiedUser);
        const authMode = verifiedUser.role === 'student' ? 'student' : 'instructor';
        setState(prev => ({
          ...prev,
          isAuthenticated: true,
          authMode,
        }));
        setIsLoading(false);
        return true;
      }
      setIsLoading(false);
      return false;
    } catch {
      setIsLoading(false);
      return false;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
      await azureAuthService.logout();
    } catch {
      // Ignore logout errors
    }
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem('virtuallab_progress');
    localStorage.removeItem('vlab_token');
    setState(initialState);
    setUser(null);
  }, []);

  const selectCourse = useCallback((courseId: string) => {
    setState(prev => ({
      ...prev,
      selectedCourseId: courseId,
    }));
  }, []);

  const setLabMode = useCallback((mode: 'guided' | 'free') => {
    setState(prev => ({
      ...prev,
      labMode: mode,
    }));
  }, []);

  const value: AppContextValue = {
    state,
    loginWithEmail,
    loginWithGoogle,
    loginWithMicrosoft,
    handleOAuthCallback,
    logout,
    selectCourse,
    setLabMode,
    isLoading,
    user,
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}

export default AppContext;
